import React from 'react';
import { useLanguage } from './LanguageContext';
import { whataLabLogo, builtOnHedera, spotifyIcon } from '../assets';
import { EXTERNAL_LINKS } from '../config';
import './Footer.css';

const Footer = () => {
  const { language } = useLanguage();
  const isEN = language === 'en';
  const year = new Date().getFullYear();

  const socialLinks = [
    {
      label: 'Twitter / X',
      href: EXTERNAL_LINKS.TWITTER,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    {
      label: 'Discord',
      href: EXTERNAL_LINKS.DISCORD,
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z"/>
        </svg>
      ),
    },
    {
      label: isEN ? 'Podcast' : 'Podcast',
      href: EXTERNAL_LINKS.SPOTIFY,
      icon: <img src={spotifyIcon} alt="Spotify" style={{ width: 18, height: 18, display: 'block' }} />,
    },
  ];

  const navLinks = [
    { label: isEN ? 'Tools' : 'Herramientas', href: '#tools' },
    { label: isEN ? 'Ecosystem' : 'Ecosistema', href: '#ecosystem' },
    { label: isEN ? 'Roadmap' : 'Roadmap', href: '#about' },
    { label: isEN ? 'FAQs' : 'Preguntas', href: '#faqs' },
    { label: isEN ? 'Contact' : 'Contacto', href: '#contact' },
  ];

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="footer">
      {/* Watermark */}
      <div className="footer-watermark" aria-hidden="true">WhataLab</div>

      <div className="container">
        <div className="footer-body">

          {/* Logo icon */}
          <div className="footer-logo-wrap">
            <img src={whataLabLogo} alt="WhataLab" className="footer-logo" />
          </div>

          {/* Headline */}
          <div className="footer-headline">
            <p className="footer-tagline">
              {isEN
                ? 'Get connected with WhataLab on social'
                : 'Conéctate con WhataLab en redes'
              }
            </p>
            <p className="footer-sub">
              {isEN ? "Don't miss our latest updates." : 'No te pierdas nuestras novedades.'}
            </p>
          </div>

          {/* Social pills */}
          <div className="footer-socials">
            {socialLinks.map(s => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-pill"
              >
                {s.icon}
                {s.label}
              </a>
            ))}
          </div>
          {/* ── Credential bar — below social buttons ── */}
          <div className="footer-cred-bar">
            <div className="footer-cred-item">
              <span className="footer-cred-label">CYBERSECURITY PARTNER</span>
              <a href="https://dedgesecurity.com/" target="_blank" rel="noopener noreferrer">
                <img src="/badges/dedge-bw.png" alt="Dedge Security" className="footer-cred-dedge" />
              </a>
            </div>
            <div className="footer-cred-divider" />
            <div className="footer-cred-item">
              <span className="footer-cred-label">CERTIFIED INNOVATIVE TECH STARTUP</span>
              <a href="https://www.enisa.es/es/certifica-tu-startup/startups" target="_blank" rel="noopener noreferrer">
                <img src="/badges/enisa.png" alt="ENISA · Gobierno de España" className="footer-cred-enisa" />
              </a>
            </div>
            <div className="footer-cred-divider" />
            <div className="footer-cred-item">
              <span className="footer-cred-label">OFFICIAL PARTNER BUILDERS PROGRAM</span>
              <a href={EXTERNAL_LINKS.HEDERA} target="_blank" rel="noopener noreferrer">
                <img src="/badges/hedera-logo-lockup.svg" alt="Hedera" className="footer-cred-hedera" />
              </a>
            </div>
          </div>

        </div>

        {/* Bottom bar */}
        <div className="footer-bottom">
          <span className="footer-copy">
            © {year} WhataLab.{' '}
            {isEN ? 'All rights reserved.' : 'Todos los derechos reservados.'}
          </span>

          <nav className="footer-nav">
            {navLinks.map(l => (
              <a key={l.label} href={l.href} className="footer-nav-link">
                {l.label}
              </a>
            ))}
          </nav>

          <button className="footer-top-btn" onClick={scrollToTop} aria-label="Back to top">
            {isEN ? 'Back to top' : 'Volver arriba'} ↑
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
